import { zodResolver } from '@hookform/resolvers/zod'
import type { HookAPI } from 'antd/es/modal/useModal'
import { useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'

import type { CardfileData } from '@/api/cardfile/cardfileApi'
import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomModalWithForm from '@/components/Atoms/AtomModalWithForm/AtomModalWithForm'
import VatLookup from '@/components/Cardfile/VatLookup/VatLookup'
import type { StoreCreationFormType } from '@/components/EntityCreationModals/StoreCreationModal/StoreCreationModal.config'
import { stockImportsApi } from '@/pages/Exchanges/tabs/StockImports/api/stockImportsApi'
import type {
  ProductSupplierDto,
  StoreModel,
} from '@/pages/Exchanges/tabs/StockImports/types/stockImports.types'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type {
  FormInputControlType,
  FormInputNameType,
  FormInputRegisterType,
} from '@/types'
import { handleApiError } from '@/utils/api/errors/handleApiError'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'

import {
  buildSupplierFormDefaults,
  buildSupplierStoreOptions,
  getStoreBindingChanges,
  getSupplierFieldsFromCardfile,
  NEW_STORE_ID_PREFIX,
  type PendingStore,
  SUPPLIER_PHONE_MASK,
  type SupplierCreationFormType,
  supplierCreationSchema,
  toPendingStoreOption,
  toProductSupplierRequest,
  VAT_NUMBER_MASK,
} from './SupplierCreationModal.config'
import cn from './SupplierCreationModal.module.scss'
import {
  applySupplierStoreBindings,
  notifyStoreBindingFailures,
} from './supplierStoreBindings'
import SupplierStoresField from './SupplierStoresField/SupplierStoresField'

const TEXT_FIELDS: Array<{ name: FormInputNameType; title: string }> = [
  { name: 'fullLegalName', title: 'Полное юридическое наименование' },
  { name: 'shortLegalName', title: 'Краткое юридическое наименование' },
  { name: 'legalAddress', title: 'Юридический адрес' },
  { name: 'postalAddress', title: 'Почтовый адрес' },
]

type SupplierCreationFormProps = {
  open: boolean
  supplier: ProductSupplierDto | null
  stores: StoreModel[]
  modal: HookAPI
  onClose: () => void
  onSaved: (supplier: ProductSupplierDto) => void
  onStoresChanged?: () => void
}

function SupplierCreationForm({
  open,
  supplier,
  stores,
  modal,
  onClose,
  onSaved,
  onStoresChanged,
}: SupplierCreationFormProps) {
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const [saving, setSaving] = useState(false)
  const [pendingStores, setPendingStores] = useState<PendingStore[]>([])
  const submittingRef = useRef(false)
  const defaults = buildSupplierFormDefaults(supplier, stores)
  const initialStoreIds = defaults.stores.map((store) => store.value)

  const {
    control,
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<SupplierCreationFormType>({
    resolver: zodResolver(supplierCreationSchema),
    defaultValues: defaults,
    mode: 'onTouched',
    shouldFocusError: false,
  })
  const vatNumber = useWatch({ control, name: 'vatNumber' })

  const inputProps = {
    layout: 'stacked' as const,
    idPrefix: 'supplier-',
    location: 'supplierCreation' as const,
    isOrganisationSelected,
    errors,
    register: register as FormInputRegisterType,
    control: control as FormInputControlType,
  }

  const handleCardfileApply = (data: CardfileData) => {
    getSupplierFieldsFromCardfile(data, getValues()).forEach(([name, value]) =>
      setValue(name, value, { shouldValidate: true, shouldDirty: true })
    )
    toast.success('Данные из картотеки подставлены')
  }

  const handleAddStore = (values: StoreCreationFormType) => {
    const pending: PendingStore = {
      id: `${NEW_STORE_ID_PREFIX}${pendingStores.length + 1}`,
      values,
    }
    setPendingStores((current) => [...current, pending])
    setValue(
      'stores',
      [...getValues('stores'), toPendingStoreOption(pending)],
      {
        shouldDirty: true,
      }
    )
  }

  const saveSupplier = async (values: SupplierCreationFormType) => {
    const selectedIds = values.stores.map((store) => store.value)
    const changes = getStoreBindingChanges(initialStoreIds, selectedIds)
    setSaving(true)
    const toastId = toast.loading(
      supplier ? 'Сохранение поставщика...' : 'Создание поставщика...'
    )
    try {
      const payload = toProductSupplierRequest(values)
      const saved = supplier
        ? await stockImportsApi.updateProductSupplier(supplier.id, payload)
        : await stockImportsApi.createProductSupplier(payload)
      toast.success(supplier ? 'Поставщик сохранён' : 'Поставщик создан', {
        id: toastId,
      })

      const { failures, changed } = await applySupplierStoreBindings(
        saved.id,
        {
          attachIds: changes.attachIds,
          detachIds: changes.detachIds,
          newStores: pendingStores.filter((store) =>
            changes.newStoreIds.includes(store.id)
          ),
        },
        stores
      )
      if (changed) onStoresChanged?.()
      notifyStoreBindingFailures(failures, Boolean(supplier))
      onSaved(saved)
    } catch (error) {
      toast.dismiss(toastId)
      handleApiError(error, modal)
    } finally {
      setSaving(false)
    }
  }

  const handleSave = async (values: SupplierCreationFormType) => {
    if (submittingRef.current) return
    submittingRef.current = true
    try {
      await saveSupplier(values)
    } finally {
      submittingRef.current = false
    }
  }

  return (
    <AtomModalWithForm
      title={supplier ? 'Редактирование поставщика' : 'Новый поставщик'}
      submitText="Сохранить"
      open={open}
      width={640}
      submitting={saving}
      maskClosable={false}
      onCancel={onClose}
      onSubmit={(event) => {
        event.preventDefault()
        void handleSubmit(handleSave, () => handleFormSubmitError(false))()
      }}
    >
      <AtomFormInput
        {...inputProps}
        name="description"
        title="Наименование"
        required
      />
      <div className={cn.vatField}>
        <AtomFormInput
          {...inputProps}
          name="vatNumber"
          title="УНП"
          placeholder="9 цифр"
          inputMode="numeric"
          mask={VAT_NUMBER_MASK}
          required
        />
        <VatLookup
          key={vatNumber}
          vatNumber={vatNumber}
          checkUniqueness={false}
          onApply={handleCardfileApply}
        />
      </div>
      {TEXT_FIELDS.map(({ name, title }) => (
        <AtomFormInput
          key={name}
          {...inputProps}
          name={name}
          title={title}
          required
        />
      ))}
      <AtomFormInput
        {...inputProps}
        name="phoneNumber"
        title="Телефон"
        placeholder="+375291234567"
        inputMode="tel"
        mask={SUPPLIER_PHONE_MASK}
        required
      />
      <AtomFormInput {...inputProps} name="email" title="Email" required />
      <SupplierStoresField
        control={control}
        errors={errors}
        options={buildSupplierStoreOptions(
          supplier?.id ?? null,
          stores,
          pendingStores
        )}
        isOrganisationSelected={isOrganisationSelected}
        onAddStore={handleAddStore}
      />
    </AtomModalWithForm>
  )
}

export default SupplierCreationForm
